"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import * as Popover from "@radix-ui/react-popover";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useRef, useState } from "react";

import { useAddDestination } from "../../_hooks/useAddDestination";
import { POPOVER_MOTION, SURFACE_FEEL } from "../../_logic/motion";
import { PlaceCombobox } from "../trip/PlaceCombobox";

/**
 * The picker behind "Add city": a city search with the org's past cities offered before typing.
 * Mounted only while open, so the past cities are asked for when someone wants them.
 * @param props.onDone - Closes the picker after a pick.
 * @returns The picker.
 * @throws Never.
 */
const AddCityPicker = ({ onDone }: { onDone: () => void }) => {
  const { add, quickPicks } = useAddDestination();
  return (
    <PlaceCombobox
      value={null}
      placeholder="Search a city"
      ariaLabel="City to compare"
      behavior={{ autoFocus: true, quickPicks, onBackspaceEmpty: onDone }}
      onPick={(place) => {
        add(place);
        onDone();
      }}
    />
  );
};

/**
 * The comparison's last header cell: "Add city", which opens a city search anchored to it, so a
 * planner who wonders "what about X?" adds it where they're looking. The new city's column takes
 * this one's place and the add column moves along.
 * @returns The header's content.
 * @throws Never.
 */
export const AddCityHeader = () => {
  // Hooks
  const anchorRef = useRef<HTMLSpanElement>(null);

  // States
  const [open, setOpen] = useState(false);

  // Handlers
  // Radix returns focus to a Popover.Trigger, and FetchButton can't be one (no ref), so focus goes
  // back to "Add city" here instead of dropping to the page.
  const returnFocus = (event: Event) => {
    event.preventDefault();
    anchorRef.current?.querySelector("button")?.focus();
  };

  // Render
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <span ref={anchorRef} className="flex justify-end">
          <FetchButton
            variant="text"
            size="sm"
            color="neutral"
            type="button"
            ariaExpanded={open}
            leftIcon={<Plus aria-hidden className="size-3.5" />}
            onClick={() => setOpen(true)}
          >
            Add city
          </FetchButton>
        </span>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={12}
          onCloseAutoFocus={returnFocus}
          className="z-50 outline-none"
        >
          <motion.div
            {...POPOVER_MOTION}
            style={{ transformOrigin: "var(--radix-popover-content-transform-origin)" }}
            className={`flex w-80 max-w-[calc(100vw-24px)] flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-3 text-left shadow-xl ${SURFACE_FEEL}`}
          >
            <AddCityPicker onDone={() => setOpen(false)} />
          </motion.div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
